import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Plus, Pencil, Trash2, Ruler, Save } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyUsage = { label_de: '', label_en: '', label_es: '', order: 0, active: true };
const emptyFeature = {
  label_de: '', label_en: '', label_es: '',
  description_de: '', description_en: '', description_es: '',
  default_checked: false, order: 0, active: true,
};

const DIM_FIELDS = [
  { key: 'planner_min_length', label: 'Min. Länge (m)' },
  { key: 'planner_max_length', label: 'Max. Länge (m)' },
  { key: 'planner_min_width', label: 'Min. Breite (m)' },
  { key: 'planner_max_width', label: 'Max. Breite (m)' },
  { key: 'planner_min_height', label: 'Min. Höhe (m)' },
  { key: 'planner_max_height', label: 'Max. Höhe (m)' },
];

export default function AdminPlanner() {
  const [usages, setUsages] = useState([]);
  const [features, setFeatures] = useState([]);
  const [dims, setDims] = useState({});
  const [dimsLoading, setDimsLoading] = useState(false);

  const [usageDialog, setUsageDialog] = useState(false);
  const [usageForm, setUsageForm] = useState({ ...emptyUsage });
  const [usageEditing, setUsageEditing] = useState(null);

  const [featureDialog, setFeatureDialog] = useState(false);
  const [featureForm, setFeatureForm] = useState({ ...emptyFeature });
  const [featureEditing, setFeatureEditing] = useState(null);

  const token = localStorage.getItem('itcs-admin-token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadUsages = () => axios.get(`${API}/admin/planner/usages`, { headers }).then(r => setUsages(r.data)).catch(() => {});
  const loadFeatures = () => axios.get(`${API}/admin/planner/features`, { headers }).then(r => setFeatures(r.data)).catch(() => {});
  const loadDims = () => axios.get(`${API}/admin/settings`, { headers }).then(r => {
    const all = r.data || {};
    const filtered = {};
    DIM_FIELDS.forEach(f => { filtered[f.key] = all[f.key] ?? ''; });
    setDims(filtered);
  }).catch(() => {});

  useEffect(() => { loadUsages(); loadFeatures(); loadDims(); }, []);

  // ---- USAGE CRUD ----
  const openNewUsage = () => { setUsageForm({ ...emptyUsage }); setUsageEditing(null); setUsageDialog(true); };
  const openEditUsage = (item) => { setUsageForm({ ...item }); setUsageEditing(item.id); setUsageDialog(true); };
  const saveUsage = async () => {
    if (!usageForm.label_de.trim()) { toast.error('Bezeichnung (DE) ist erforderlich'); return; }
    try {
      if (usageEditing) await axios.put(`${API}/admin/planner/usages/${usageEditing}`, usageForm, { headers });
      else await axios.post(`${API}/admin/planner/usages`, usageForm, { headers });
      toast.success('Gespeichert');
      setUsageDialog(false); loadUsages();
    } catch { toast.error('Fehler beim Speichern'); }
  };
  const deleteUsage = async (id) => {
    if (!window.confirm('Nutzungsart wirklich löschen?')) return;
    try { await axios.delete(`${API}/admin/planner/usages/${id}`, { headers }); toast.success('Gelöscht'); loadUsages(); }
    catch { toast.error('Fehler'); }
  };

  // ---- FEATURE CRUD ----
  const openNewFeature = () => { setFeatureForm({ ...emptyFeature }); setFeatureEditing(null); setFeatureDialog(true); };
  const openEditFeature = (item) => { setFeatureForm({ ...item }); setFeatureEditing(item.id); setFeatureDialog(true); };
  const saveFeature = async () => {
    if (!featureForm.label_de.trim()) { toast.error('Bezeichnung (DE) ist erforderlich'); return; }
    try {
      if (featureEditing) await axios.put(`${API}/admin/planner/features/${featureEditing}`, featureForm, { headers });
      else await axios.post(`${API}/admin/planner/features`, featureForm, { headers });
      toast.success('Gespeichert');
      setFeatureDialog(false); loadFeatures();
    } catch { toast.error('Fehler beim Speichern'); }
  };
  const deleteFeature = async (id) => {
    if (!window.confirm('Ausstattungsmerkmal wirklich löschen?')) return;
    try { await axios.delete(`${API}/admin/planner/features/${id}`, { headers }); toast.success('Gelöscht'); loadFeatures(); }
    catch { toast.error('Fehler'); }
  };

  // ---- DIMENSIONS ----
  const saveDims = async () => {
    setDimsLoading(true);
    try {
      await Promise.all(DIM_FIELDS.map(f => axios.put(`${API}/admin/settings`, { key: f.key, value: String(dims[f.key] ?? '') }, { headers })));
      toast.success('Abmessungs-Limits gespeichert');
    } catch { toast.error('Fehler beim Speichern'); }
    setDimsLoading(false);
  };

  return (
    <div data-testid="admin-planner">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#0F172A]">Hallenplaner verwalten</h1>
        <p className="text-sm text-slate-500 mt-1">Optionen, Ausstattung und Abmessungs-Grenzen des Online-Hallenplaners konfigurieren</p>
      </div>

      <Tabs defaultValue="usages">
        <TabsList>
          <TabsTrigger value="usages" data-testid="tab-usages">Nutzungsarten</TabsTrigger>
          <TabsTrigger value="features" data-testid="tab-features">Ausstattung</TabsTrigger>
          <TabsTrigger value="dimensions" data-testid="tab-dimensions">Abmessungen</TabsTrigger>
        </TabsList>

        {/* USAGES TAB */}
        <TabsContent value="usages" className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-slate-600">{'Optionen im Dropdown „Nutzungsart" (Schritt 2 des Hallenplaners)'}</p>
            <Button onClick={openNewUsage} className="bg-[#066ce7] hover:bg-[#005bb5] text-white" data-testid="add-usage-btn"><Plus className="w-4 h-4 mr-2" /> Neue Nutzungsart</Button>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Bezeichnung (DE)</TableHead><TableHead>EN</TableHead><TableHead>ES</TableHead><TableHead>Reihenfolge</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aktionen</TableHead></TableRow></TableHeader>
              <TableBody>
                {usages.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-400 py-8">Keine Nutzungsarten angelegt.</TableCell></TableRow>}
                {usages.map(u => (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">{u.label_de}</TableCell>
                    <TableCell className="text-sm text-slate-500">{u.label_en || '—'}</TableCell>
                    <TableCell className="text-sm text-slate-500">{u.label_es || '—'}</TableCell>
                    <TableCell className="text-sm text-slate-500">{u.order}</TableCell>
                    <TableCell>{u.active ? <Badge className="bg-emerald-100 text-emerald-700">Aktiv</Badge> : <Badge variant="outline">Inaktiv</Badge>}</TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button variant="ghost" size="sm" onClick={() => openEditUsage(u)}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteUsage(u.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* FEATURES TAB */}
        <TabsContent value="features" className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-slate-600">{'Checkboxen im Bereich „Ausstattung" (Schritt 3 des Hallenplaners)'}</p>
            <Button onClick={openNewFeature} className="bg-[#066ce7] hover:bg-[#005bb5] text-white" data-testid="add-feature-btn"><Plus className="w-4 h-4 mr-2" /> Neue Ausstattung</Button>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Bezeichnung (DE)</TableHead><TableHead>EN</TableHead><TableHead>ES</TableHead><TableHead>Reihenf.</TableHead><TableHead>Standard</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aktionen</TableHead></TableRow></TableHeader>
              <TableBody>
                {features.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-sm text-slate-400 py-8">Keine Ausstattungsmerkmale angelegt.</TableCell></TableRow>}
                {features.map(f => (
                  <TableRow key={f.id}>
                    <TableCell className="font-medium">{f.label_de}</TableCell>
                    <TableCell className="text-sm text-slate-500">{f.label_en || '—'}</TableCell>
                    <TableCell className="text-sm text-slate-500">{f.label_es || '—'}</TableCell>
                    <TableCell className="text-sm text-slate-500">{f.order}</TableCell>
                    <TableCell>{f.default_checked ? <Badge className="bg-blue-100 text-blue-700">✓ vorausgewählt</Badge> : <span className="text-xs text-slate-400">—</span>}</TableCell>
                    <TableCell>{f.active ? <Badge className="bg-emerald-100 text-emerald-700">Aktiv</Badge> : <Badge variant="outline">Inaktiv</Badge>}</TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button variant="ghost" size="sm" onClick={() => openEditFeature(f)}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteFeature(f.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* DIMENSIONS TAB */}
        <TabsContent value="dimensions" className="mt-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-2xl">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center"><Ruler className="w-5 h-5 text-[#066ce7]" /></div>
              <div>
                <h2 className="text-base font-semibold text-[#0F172A]">Erlaubte Abmessungen</h2>
                <p className="text-xs text-slate-500">{'Grenzwerte für Schritt 1 des Hallenplaners. Werte leer lassen für „keine Grenze".'}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {DIM_FIELDS.map(f => (
                <div key={f.key}>
                  <Label className="text-xs">{f.label}</Label>
                  <Input type="number" value={dims[f.key] ?? ''} onChange={e => setDims(prev => ({ ...prev, [f.key]: e.target.value }))} data-testid={`dim-${f.key}`} />
                </div>
              ))}
            </div>
            <Button onClick={saveDims} disabled={dimsLoading} className="bg-[#066ce7] hover:bg-[#005bb5] text-white mt-6" data-testid="save-dims-btn">
              <Save className="w-4 h-4 mr-2" /> {dimsLoading ? 'Speichern...' : 'Abmessungen speichern'}
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      {/* USAGE DIALOG */}
      <Dialog open={usageDialog} onOpenChange={setUsageDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{usageEditing ? 'Nutzungsart bearbeiten' : 'Neue Nutzungsart'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs">Bezeichnung DE *</Label><Input value={usageForm.label_de} onChange={e => setUsageForm(p => ({ ...p, label_de: e.target.value }))} placeholder="z.B. Kühlhalle" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">EN</Label><Input value={usageForm.label_en} onChange={e => setUsageForm(p => ({ ...p, label_en: e.target.value }))} /></div>
              <div><Label className="text-xs">ES</Label><Input value={usageForm.label_es} onChange={e => setUsageForm(p => ({ ...p, label_es: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Reihenfolge</Label><Input type="number" value={usageForm.order} onChange={e => setUsageForm(p => ({ ...p, order: parseInt(e.target.value) || 0 }))} /></div>
              <div className="flex items-center gap-3 pt-5"><Switch checked={usageForm.active} onCheckedChange={v => setUsageForm(p => ({ ...p, active: v }))} /><Label className="text-xs">Aktiv</Label></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUsageDialog(false)}>Abbrechen</Button>
            <Button onClick={saveUsage} className="bg-[#066ce7] text-white" data-testid="save-usage-btn">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FEATURE DIALOG */}
      <Dialog open={featureDialog} onOpenChange={setFeatureDialog}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{featureEditing ? 'Ausstattungsmerkmal bearbeiten' : 'Neues Ausstattungsmerkmal'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs">Bezeichnung DE *</Label><Input value={featureForm.label_de} onChange={e => setFeatureForm(p => ({ ...p, label_de: e.target.value }))} placeholder="z.B. Kranbahn" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">EN</Label><Input value={featureForm.label_en} onChange={e => setFeatureForm(p => ({ ...p, label_en: e.target.value }))} /></div>
              <div><Label className="text-xs">ES</Label><Input value={featureForm.label_es} onChange={e => setFeatureForm(p => ({ ...p, label_es: e.target.value }))} /></div>
            </div>
            <div>
              <Label className="text-xs">Beschreibung DE <span className="text-slate-400">(optional, als Tooltip)</span></Label>
              <Textarea value={featureForm.description_de} onChange={e => setFeatureForm(p => ({ ...p, description_de: e.target.value }))} rows={2} placeholder="Kurze Erklärung für den Kunden" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Beschreibung EN</Label><Textarea value={featureForm.description_en} onChange={e => setFeatureForm(p => ({ ...p, description_en: e.target.value }))} rows={2} /></div>
              <div><Label className="text-xs">Beschreibung ES</Label><Textarea value={featureForm.description_es} onChange={e => setFeatureForm(p => ({ ...p, description_es: e.target.value }))} rows={2} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Reihenfolge</Label><Input type="number" value={featureForm.order} onChange={e => setFeatureForm(p => ({ ...p, order: parseInt(e.target.value) || 0 }))} /></div>
              <div className="flex items-center gap-2 pt-5"><Switch checked={featureForm.default_checked} onCheckedChange={v => setFeatureForm(p => ({ ...p, default_checked: v }))} /><Label className="text-xs">Standardmäßig ausgewählt</Label></div>
              <div className="flex items-center gap-2 pt-5"><Switch checked={featureForm.active} onCheckedChange={v => setFeatureForm(p => ({ ...p, active: v }))} /><Label className="text-xs">Aktiv</Label></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFeatureDialog(false)}>Abbrechen</Button>
            <Button onClick={saveFeature} className="bg-[#066ce7] text-white" data-testid="save-feature-btn">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
